import { useTelegramBackButton } from '@common/hooks';
import { Loader } from '@common/ui/Loader';
import { Result } from '@common/ui/Result';

import { ProfileCard } from '@entities/dating/ui/ProfileCard';
import { ProfileInfo } from '@entities/dating/ui/ProfileInfo';

import { useGetUserById } from '@services/hooks/UsersService';
import { type User } from '@services/UsersService';

type Props = {
  profileId: string;
  canSwipes?: boolean;
};

/**
 * Detects the moderation-stub shape returned by GET /api/v1/user/:id when the
 * target user is in `pending_update` (server side: api.v1.userDetail.js).
 *
 * The moderation stub returns only:
 *   { user_id: string, approval_status: 'pending_update' }
 * — i.e. no photos / username / age / city. ProfileCard / ProfileInfo access
 * several un-guarded fields on the profile, so rendering with the stub
 * crashed inside React and bubbled up as "Error caused error boundary"
 * (Sentry issue #70294076).
 *
 * Detection note. Approved profiles served by the same endpoint also expose
 * `approval_status` as a flat string ('approved') — the server uses
 * `standardizeUserProfile(..., { includeFeedExtensions: true })`, where
 * `approval_status` is typed as `string`. An earlier version of this guard
 * keyed only on `typeof status === 'string'`, which silently triggered the
 * moderation screen for EVERY profile tap on the new client build (users
 * reported "see only the moderation screen on any profile"). Narrow the
 * check to the specific `pending_update` literal.
 */
const isModerationStub = (
  data: User | undefined,
): data is User & { approval_status: 'pending_update' } => {
  if (!data || typeof data !== 'object') return false;
  const status = (data as { approval_status?: unknown }).approval_status;
  return status === 'pending_update';
};

export const Profile = ({ profileId }: Props) => {
  const { data: dataUserById, isLoading: isLoadingUserById } = useGetUserById({
    queryParams: {
      userId: profileId,
    },
  });

  useTelegramBackButton({ to: 'back', enabled: Boolean(dataUserById) });

  if (isLoadingUserById) {
    return <Loader />;
  }

  if (!dataUserById) {
    return null;
  }

  if (isModerationStub(dataUserById)) {
    return (
      <Result
        src="/icons/3d-icons/sand-clock.png"
        title="Профиль на модерации"
        description="Этот профиль сейчас обновляется. Загляните чуть позже."
      />
    );
  }

  return (
    <>
      <div
        style={{
          position: 'relative',
          height: 'calc(100vh - 66px - 8px)',
          overflow: 'hidden',
        }}
      >
        <ProfileCard profile={dataUserById} canSwipes={false} />
      </div>
      <div>
        <ProfileInfo profile={dataUserById} />
      </div>
    </>
  );
};
